import { Box, Card, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { categoryIcon } from "./icons.js";
import { kbFolderPath } from "./kb-paths.js";
import type { DocCategory } from "./types.js";

/**
 * One folder as a card — on the landing page (the top-level folders) and on
 * a folder page (its sub-folders), so a folder reads the same at every depth.
 * `count` is everything readable in it AND below it (`folderCount`).
 */
export function FolderCard({
  category,
  count,
  subFolderCount = 0,
}: {
  category: DocCategory;
  count: number;
  subFolderCount?: number;
}): JSX.Element {
  const Icon = categoryIcon(category.icon);
  const parts = [
    ...(subFolderCount > 0 ? [`${subFolderCount} ${subFolderCount === 1 ? "folder" : "folders"}`] : []),
    `${count} ${count === 1 ? "article" : "articles"}`,
  ];
  return (
    <Card
      component={RouterLink}
      to={kbFolderPath(category.slug)}
      data-testid={`kb-folder-${category.slug}`}
      sx={folderCardSx}
    >
      <Box aria-hidden sx={folderIconSx}>
        <Icon size={18} />
      </Box>
      <Typography component="h4" sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
        {category.name}
      </Typography>
      {category.description && (
        <Typography
          sx={{
            fontSize: 12.5,
            color: "text.secondary",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {category.description}
        </Typography>
      )}
      <Typography sx={{ fontSize: 12, color: "text.disabled" }}>{parts.join(" · ")}</Typography>
    </Card>
  );
}

/** The grid folder cards sit in. */
export function FolderGrid({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2 }}>
      {children}
    </Box>
  );
}

export const folderCardSx: SxProps<Theme> = {
  display: "grid",
  alignContent: "start",
  gap: 1,
  p: 2.5,
  textDecoration: "none",
  color: "text.primary",
  transition: (t: Theme) => t.transitions.create(["background-color", "border-color"], { duration: 150 }),
  "&:hover": { bgcolor: "action.hover" },
};

export const folderIconSx: SxProps<Theme> = {
  display: "grid",
  placeItems: "center",
  width: 36,
  height: 36,
  borderRadius: "50%",
  bgcolor: "surfaceSoft",
  color: "primary.main",
};
